import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_ITEMCOMPONENTTYPES_LOAD,
    ACTION_ITEMCOMPONENTTYPES_LOAD_BY_ID,
    ACTION_ITEMCOMPONENTTYPES_LOAD_FAILURE,
    ACTION_ITEMCOMPONENTTYPES_LOAD_SUCCESS,

    ACTION_ITEMCOMPONENTTYPES_SAVE,
    ACTION_ITEMCOMPONENTTYPES_SAVE_SUCCESS,
    ACTION_ITEMCOMPONENTTYPES_SAVE_FAILURE,

    ACTION_ITEMCOMPONENTTYPES_CREATE,
    ACTION_ITEMCOMPONENTTYPES_CREATE_SUCCESS,
    ACTION_ITEMCOMPONENTTYPES_CREATE_FAILURE,

    ACTION_ITEMCOMPONENTTYPES_CLEAR,
    ACTION_ITEMCOMPONENTTYPES_FILTER,

    ItemComponentTypeAction
} from '../actions/itemComponentType';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IItemComponentTypeState {
    formData: Dtos.ItemComponentType | Dtos.ItemComponentType[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.ItemComponentType[];
    formLength: number;
}

const initialState: IItemComponentTypeState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IItemComponentTypeState, isLoading: boolean): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IItemComponentTypeState,
    formData: Dtos.ItemComponentType | Dtos.ItemComponentType[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IItemComponentTypeState, formFilter: IFormFilter): IItemComponentTypeState {

    const items: Dtos.ItemComponentType[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IItemComponentTypeState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IItemComponentTypeState, responseStatus: Dtos.ResponseStatus): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IItemComponentTypeState, propertiesToClear?: string[]): IItemComponentTypeState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IItemComponentTypeState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IItemComponentTypeState, isSaving: boolean): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IItemComponentTypeState,
    formData: Dtos.ItemComponentType | Dtos.ItemComponentType[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IItemComponentTypeState, responseStatus: Dtos.ResponseStatus): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IItemComponentTypeState, isCreating: boolean): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IItemComponentTypeState,
    formData: Dtos.ItemComponentType,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IItemComponentTypeState, responseStatus: Dtos.ResponseStatus): IItemComponentTypeState {
    const newState: IItemComponentTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const ItemComponentTypeReducer: Reducer<IItemComponentTypeState> = (state: IItemComponentTypeState = initialState, action: ItemComponentTypeAction) => {
    switch (action.type) {
        case ACTION_ITEMCOMPONENTTYPES_LOAD:
        case ACTION_ITEMCOMPONENTTYPES_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_ITEMCOMPONENTTYPES_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
       case ACTION_ITEMCOMPONENTTYPES_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_ITEMCOMPONENTTYPES_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_ITEMCOMPONENTTYPES_SAVE:
            return saving(state, true);
        case ACTION_ITEMCOMPONENTTYPES_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_ITEMCOMPONENTTYPES_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_ITEMCOMPONENTTYPES_CREATE:
            return creating(state, true);
        case ACTION_ITEMCOMPONENTTYPES_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_ITEMCOMPONENTTYPES_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_ITEMCOMPONENTTYPES_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default ItemComponentTypeReducer;